/**
 * Knowledge-graph documents, read inside PlanHub. (PH-72)
 *
 * Or, 2026-08-29: *"why planhub not use frontend link and request api to backend and backend get from
 * gitlab KG of html instead — this cannot view on mobile"*.
 *
 * 🔴 HE IS RIGHT, AND THE MOBILE FAILURE HAS A CONCRETE CAUSE.
 *   I first served the rendered page straight from the API at `planhub-api.tradebeam.xyz`. The
 *   session cookie is set **by the API, on the API's own host, with no `domain` and `sameSite: lax`,
 *   during a cross-origin call from the web app.** A mobile browser applying third-party cookie
 *   partitioning does not send that cookie when you type the API URL directly ⇒ **401, a login wall,
 *   or a blank page** — exactly what he saw.
 *
 * ⭐ AND THE ARCHITECTURE POINT STANDS WITHOUT THE BUG: every other surface here goes
 *   **frontend → API → source**. A raw API link was a shortcut, and shortcuts around the auth
 *   boundary are precisely where auth breaks.
 *
 * ⇒ This view lives behind `RequireAuth` like every other page, calls the API through the shared
 *   `request()` helper (which sends `credentials: 'include'` from the web origin the cookie was
 *   issued for), and renders the HTML the API returns.
 *
 * ⛔ IT DOES NOT RENDER MARKDOWN ITSELF. The API is the single renderer — a second one here is the
 *   divergence this whole feature exists to prevent.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  fetchDoc,
  fetchDocIndex,
  type DocIndexResponse,
  type DocResponse,
} from '../../api/endpoints';
import { ApiError } from '../../api/errors';

/**
 * The most useful sentence we have about a failure.
 *
 * 🔴 FOUND BY A TEST, AND IT WAS A REAL DEFECT NOT A TEST PROBLEM. This view originally read
 *   `e.message`, which on an `ApiError` is a GENERIC string chosen by kind — "something went
 *   wrong"-shaped. The API's own explanation ("knowledge-graph reading is not configured on this
 *   deployment", "gitlab is unreachable") lives in `.reason`, and reading the wrong field threw
 *   away the only part that tells a reader what to do.
 * ⭐ `.reason` first, `.message` as the fallback: a named cause lets someone fix a mistyped path
 *   themselves; a generic one sends them to us.
 */
function reasonFor(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.reason) return error.reason;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

type State =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; doc: DocResponse }
  | { status: 'error'; message: string };

type IndexState =
  | { status: 'loading' }
  | { status: 'ready'; index: DocIndexResponse }
  | { status: 'error'; message: string };

/**
 * The document index — what you see at `/docs` with no path. (PH-72 req 4)
 *
 * ⭐ WHY THIS IS A REQUIREMENT AND NOT A NICETY: before it existed, a document was reachable only
 *   by someone who already had its path. "A link works when handed to you" and "you can find a
 *   document yourself" are different capabilities, and the ticket asked for the second.
 */
function DocIndex() {
  const [state, setState] = useState<IndexState>({ status: 'loading' });
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchDocIndex()
      .then((index) => {
        if (!cancelled) setState({ status: 'ready', index });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setState({ status: 'error', message: reasonFor(e, 'could not load the document index') });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'loading') return <div className="docs-empty">Loading documents…</div>;
  if (state.status === 'error') {
    return (
      <div className="docs-empty">
        <h1>Documents</h1>
        <p>{state.message}</p>
      </div>
    );
  }

  const needle = filter.trim().toLowerCase();
  // ⭐ Filters on the PATH as well as the title, because the path carries the folder — which is how
  //   someone looking for "the planhub plans" actually thinks about it.
  const groups = state.index.groups
    .map((group) => ({
      ...group,
      documents: needle
        ? group.documents.filter(
            (d) =>
              d.title.toLowerCase().includes(needle) || d.path.toLowerCase().includes(needle),
          )
        : group.documents,
    }))
    .filter((group) => group.documents.length > 0);

  return (
    <div className="docs-index">
      <h1>Documents</h1>
      <p className="docs-hint">
        {state.index.total} document{state.index.total === 1 ? '' : 's'} from the knowledge graph at{' '}
        <code>{state.index.ref}</code>. Rendered here; edited in the KG.
      </p>

      <input
        className="docs-filter"
        type="search"
        placeholder="Filter by title or folder…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        aria-label="Filter documents"
      />

      {groups.length === 0 ? (
        <p className="docs-hint">Nothing matches “{filter}”.</p>
      ) : (
        groups.map((group) => (
          <section key={group.root} className="docs-group">
            <h2>
              {group.root} <span className="docs-count">{group.documents.length}</span>
            </h2>
            <ul>
              {group.documents.map((doc) => (
                <li key={doc.path}>
                  <Link to={`/docs?path=${encodeURIComponent(doc.path)}`}>{doc.title}</Link>
                  <span className="docs-path">{doc.path}</span>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

export function DocsView() {
  const [params] = useSearchParams();
  const path = params.get('path') ?? '';
  const [state, setState] = useState<State>({ status: 'idle' });

  useEffect(() => {
    if (!path) {
      setState({ status: 'idle' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    fetchDoc(path)
      .then((doc) => {
        if (!cancelled) setState({ status: 'ready', doc });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // ⭐ Say what failed and what to do. "Something went wrong" sends the reader to us; a named
        //   cause lets them fix a mistyped path themselves — which is why this reads `.reason`
        //   (the API's own sentence) before falling back to the generic `.message`.
        setState({ status: 'error', message: reasonFor(e, 'could not load this document') });
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  // ⭐ No path ⇒ the INDEX, not an instruction telling the reader to go find a path themselves.
  //   The old version printed "open a document by path, for example…", which is a help message
  //   standing in for the feature it is apologising for.
  if (!path) return <DocIndex />;

  if (state.status === 'loading') return <div className="docs-empty">Loading…</div>;

  if (state.status === 'error') {
    return (
      <div className="docs-empty">
        <h1>Could not open this document</h1>
        <p>{state.message}</p>
        <p className="docs-hint">Path requested: <code>{path}</code></p>
      </div>
    );
  }

  if (state.status !== 'ready') return null;

  return (
    <article className="docs-body">
      <nav className="docs-nav">
        <Link to="/docs">← All documents</Link>
        {/*
          ⭐ THE BACK-LINK (PH-72 req 2), document → ticket. The other direction is an ordinary
             PlanHub ticket link on the ticket itself. Either half alone strands a reader: a plan
             with no ticket, or a ticket with no plan.
          ⛔ Rendered only when the API RESOLVED the key. A document naming a deleted or renumbered
             ticket gets no link rather than a link to nowhere — `ticket: null` is a normal answer,
             not a failure, because documents live in git and tickets in Postgres.
        */}
        {state.doc.ticket && (
          <Link className="docs-ticket" to={`/backlog?ticket=${state.doc.ticket.id}`}>
            {state.doc.ticket.key} · {state.doc.ticket.title}
          </Link>
        )}
      </nav>
      {/*
        ⛔ The HTML comes from the API's renderer, which ESCAPES ITS SOURCE BEFORE applying structure
           — no source character can become a tag. That property is what makes this safe, and it
           lives in the API (src/docs/render.ts). If that renderer is ever replaced, the replacement
           must preserve escape-first or this line becomes an injection point.
      */}
      <div dangerouslySetInnerHTML={{ __html: state.doc.html }} />
      <footer className="docs-prov">
        <span>source {state.doc.path}</span>
        <span>ref {state.doc.ref}</span>
        <span>rendered {state.doc.renderedAt}</span>
      </footer>
    </article>
  );
}
